import React, { FC } from 'react';
import { Editor } from '@tiptap/react';
import {
  FaBold,
  FaItalic,
  FaStrikethrough,
  FaListUl,
  FaListOl,
  FaImage,
} from 'react-icons/fa';

interface TopToolbarProps {
  editor: Editor | null;
}

// Shared icon type + casts to keep TS happy
type IconComponent = React.ComponentType<{ className?: string }>;

const BoldIcon = FaBold as IconComponent;
const ItalicIcon = FaItalic as IconComponent;
const StrikeIcon = FaStrikethrough as IconComponent;
const BulletListIcon = FaListUl as IconComponent;
const OrderedListIcon = FaListOl as IconComponent;
const ImageIcon = FaImage as IconComponent;

const TopToolbar: FC<TopToolbarProps> = ({ editor }) => {
  if (!editor) {
    return null;
  }

  const addImage = () => {
    editor.chain().focus().openImageFilePicker().run();
  };

  const headingValue =
    editor.isActive('heading', { level: 1 }) ? 'h1'
      : editor.isActive('heading', { level: 2 }) ? 'h2'
      : editor.isActive('heading', { level: 3 }) ? 'h3'
      : editor.isActive('heading', { level: 4 }) ? 'h4'
      : 'paragraph';

  return (
    <div className="top-toolbar">
      <div className="toolbar-group">
        <select
          value={headingValue}
          onChange={(e) => {
            const level = e.target.value;
            if (level === 'paragraph') {
              editor.chain().focus().setParagraph().run();
            } else {
              editor
                .chain()
                .focus()
                .toggleHeading({ level: parseInt(level.substring(1)) as 1 | 2 | 3 | 4 } as any) // Cast to any to bypass TS error for literal types
                .run();
            }
          }}
          className="toolbar-select"
        >
          <option value="paragraph">Paragraph</option>
          <option value="h1">Heading 1</option>
          <option value="h2">Heading 2</option>
          <option value="h3">Heading 3</option>
          <option value="h4">Heading 4</option>
        </select>

        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`toolbar-button ${editor.isActive('bold') ? 'is-active' : ''}`}
          title="Bold"
        >
          <BoldIcon />
        </button>

        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`toolbar-button ${editor.isActive('italic') ? 'is-active' : ''}`}
          title="Italic"
        >
          <ItalicIcon />
        </button>

        <button
          type="button"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          className={`toolbar-button ${editor.isActive('strike') ? 'is-active' : ''}`}
          title="Strikethrough"
        >
          <StrikeIcon />
        </button>

        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`toolbar-button ${editor.isActive('bulletList') ? 'is-active' : ''}`}
          title="Bullet List"
        >
          <BulletListIcon />
        </button>

        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`toolbar-button ${editor.isActive('orderedList') ? 'is-active' : ''}`}
          title="Ordered List"
        >
          <OrderedListIcon />
        </button>

        <button
          type="button"
          onClick={addImage}
          className="toolbar-button"
          title="Insert Image"
        >
          <ImageIcon />
        </button>
      </div>

      <div className="toolbar-group">
        {/* The page title input and publish button have been moved to WikiEditorWrapper */}
      </div>
    </div>
  );
};

export default TopToolbar;
